// Feature pipeline (GEN-8): trees then ground vegetation, in fixed data order so
// the world is seed-stable. Runs after carving/ores so plants sit on final
// terrain. PURE (ENG-1): height/biome come in as closures over the same noise the
// surface pass uses, so a chunk's features match on the main thread and the worker.
//
// Cross-chunk canopies use the planner-lite margin technique: to fill chunk T we
// re-plan every source chunk S in T's 3×3 neighborhood (deterministic from S's
// coords) and stamp only the voxels that land in T. A tree's origin always lies in
// exactly one S, so it is never double-placed, and both T and its neighbor compute
// the identical tree — seam-free without loading each other.
import { WORLD } from '../../config';
import { Rng } from '../../engine/rng';
import { STATE_AIR } from '../../engine/section';
import type { Chunk } from '../../engine/chunk';
import type { Vegetation, PatchRule } from '../../registry/schemas';
import { buildTree, pickKind, TREE_BLOCKS, type TreeKind, type TreeVoxel } from './trees';

export interface FeatureDeps {
  seed: number | string | bigint;
  vegetation: Vegetation;
  /** name → default block state id (also resolves `name[axis=y]` keys). */
  resolve: (name: string) => number;
  /** Terrain surface height at world (x,z) — same value the surface pass paints. */
  heightAt: (x: number, z: number) => number;
  biomeNameAt: (x: number, z: number) => string;
  isBlocking: (s: number) => boolean;
  waterState: number;
}

interface ResolvedPatch {
  rule: PatchRule;
  state: number;
  biomes: ReadonlySet<string>;
  on: ReadonlySet<number>;
}

const TREE_KINDS: readonly TreeKind[] = ['oak', 'birch', 'spruce', 'cherry'];

/** floor(v) with a fractional-probability extra — exactly one rng draw. */
function attemptCount(v: number, rng: Rng): number {
  const base = Math.floor(v);
  return base + (rng.nextFloat() < v - base ? 1 : 0);
}

export class FeatureGen {
  private readonly baseRng: Rng;
  private readonly trees: Vegetation['trees'];
  private readonly patches: readonly ResolvedPatch[];
  private readonly treeStates: Record<TreeKind, { log: number; leaf: number }>;
  private readonly leafStates: ReadonlySet<number>;

  constructor(private readonly deps: FeatureDeps) {
    this.baseRng = new Rng(deps.seed);
    this.trees = deps.vegetation.trees;
    this.patches = deps.vegetation.patches.map((rule) => ({
      rule,
      state: deps.resolve(rule.block),
      biomes: new Set(rule.biomes),
      on: new Set(rule.on.map((n) => deps.resolve(n))),
    }));
    const states = {} as Record<TreeKind, { log: number; leaf: number }>;
    const leaves = new Set<number>();
    for (const k of TREE_KINDS) {
      const log = deps.resolve(`${TREE_BLOCKS[k].log}[axis=y]`);
      const leaf = deps.resolve(TREE_BLOCKS[k].leaves);
      states[k] = { log, leaf };
      leaves.add(leaf);
    }
    this.treeStates = states;
    this.leafStates = leaves;
  }

  /** Populate `chunk` with trees (incl. canopies from neighbors) then patches. */
  place(chunk: Chunk): void {
    if (this.trees.length > 0) {
      for (let dcz = -1; dcz <= 1; dcz++) {
        for (let dcx = -1; dcx <= 1; dcx++) {
          this.plantTrees(chunk, chunk.cx + dcx, chunk.cz + dcz);
        }
      }
    }
    if (this.patches.length > 0) this.scatterPatches(chunk);
  }

  // Plan the trees rooted in source chunk (scx,scz) and stamp their voxels into
  // `target` (whichever chunk we are generating).
  private plantTrees(target: Chunk, scx: number, scz: number): void {
    const rng = this.baseRng.forkAt('trees', scx, scz);
    const sbx = scx * WORLD.SECTION, sbz = scz * WORLD.SECTION;
    for (const rule of this.trees) {
      const n = attemptCount(rule.perChunk, rng);
      for (let i = 0; i < n; i++) {
        const lx = rng.nextInt(WORLD.SECTION), lz = rng.nextInt(WORLD.SECTION);
        const wx = sbx + lx, wz = sbz + lz;
        if (!rule.biomes.includes(this.deps.biomeNameAt(wx, wz))) continue;
        const groundY = this.deps.heightAt(wx, wz);
        if (groundY < WORLD.SEA_LEVEL || groundY + 14 > WORLD.MAX_Y) continue;
        const kind = pickKind(rule.types, rng);
        const voxels = buildTree(kind, wx, wz, groundY, rng);
        this.stamp(target, voxels, this.treeStates[kind].log, this.treeStates[kind].leaf);
      }
    }
  }

  private stamp(target: Chunk, voxels: readonly TreeVoxel[], logState: number, leafState: number): void {
    const bx = target.cx * WORLD.SECTION, bz = target.cz * WORLD.SECTION;
    for (const v of voxels) {
      const lx = v.x - bx, lz = v.z - bz;
      if (lx < 0 || lx >= WORLD.SECTION || lz < 0 || lz >= WORLD.SECTION) continue;
      if (v.y < WORLD.MIN_Y || v.y > WORLD.MAX_Y) continue;
      const cur = target.getBlockState(lx, v.y, lz);
      if (v.leaf) {
        if (cur === STATE_AIR) target.setBlockState(lx, v.y, lz, leafState, this.deps.isBlocking);
      } else if (cur === STATE_AIR || this.leafStates.has(cur)) {
        target.setBlockState(lx, v.y, lz, logState, this.deps.isBlocking);
      }
    }
  }

  private scatterPatches(chunk: Chunk): void {
    const rng = this.baseRng.forkAt('patches', chunk.cx, chunk.cz);
    const bx = chunk.cx * WORLD.SECTION, bz = chunk.cz * WORLD.SECTION;
    for (const { rule, state, biomes, on } of this.patches) {
      const n = attemptCount(rule.perChunk, rng);
      for (let i = 0; i < n; i++) {
        const lx = rng.nextInt(WORLD.SECTION), lz = rng.nextInt(WORLD.SECTION);
        const wx = bx + lx, wz = bz + lz;
        if (!biomes.has(this.deps.biomeNameAt(wx, wz))) continue;
        const groundY = this.deps.heightAt(wx, wz);
        if (groundY < WORLD.SEA_LEVEL || groundY + 4 > WORLD.MAX_Y) continue;
        if (!on.has(chunk.getBlockState(lx, groundY, lz))) continue; // surface breached / wrong top
        if (chunk.getBlockState(lx, groundY + 1, lz) !== STATE_AIR) continue; // occupied (e.g. a trunk)
        if (rule.nextToWater && !this.nearWater(wx, wz, groundY)) continue;
        if (rule.clearNeighbors && !this.clearNeighbors(wx, wz, groundY)) continue;
        const h = rule.minHeight === rule.maxHeight
          ? rule.minHeight
          : rule.minHeight + rng.nextInt(rule.maxHeight - rule.minHeight + 1);
        for (let k = 0; k < h; k++) {
          const y = groundY + 1 + k;
          if (y > WORLD.MAX_Y || chunk.getBlockState(lx, y, lz) !== STATE_AIR) break;
          chunk.setBlockState(lx, y, lz, state, this.deps.isBlocking);
        }
      }
    }
  }

  // A water block sits horizontally next to the planted-on cell (groundY) — true
  // when a neighbor column's surface is lower and this cell is at/under sea level.
  private nearWater(wx: number, wz: number, groundY: number): boolean {
    if (groundY > WORLD.SEA_LEVEL) return false;
    return (
      this.deps.heightAt(wx + 1, wz) < groundY ||
      this.deps.heightAt(wx - 1, wz) < groundY ||
      this.deps.heightAt(wx, wz + 1) < groundY ||
      this.deps.heightAt(wx, wz - 1) < groundY
    );
  }

  // No neighbor column rises above the base — cactus can't be walled in/buried.
  private clearNeighbors(wx: number, wz: number, groundY: number): boolean {
    return (
      this.deps.heightAt(wx + 1, wz) <= groundY &&
      this.deps.heightAt(wx - 1, wz) <= groundY &&
      this.deps.heightAt(wx, wz + 1) <= groundY &&
      this.deps.heightAt(wx, wz - 1) <= groundY
    );
  }
}
